// Browser-side conversion tracking: stores campaign attribution, pushes GTM events and
// builds the tracking payload every lead form and WhatsApp link sends. Client components only.

import {
  buildGenerateLeadEvent,
  buildRefId,
  buildThankYouUrl,
  getOrigen,
  resolveAttributionRecord,
  sanitizeAttribution,
  ATTRIBUTION_TTL_MS,
  type Attribution,
  type AttributionRecord,
  type LeadLocale,
  type LeadTrackingPayload,
  type Origen,
} from '@/lib/lead-tracking';
import { CONSENT_STORAGE_KEY } from '@/lib/consent';

export { CONSENT_STORAGE_KEY };

const ATTRIBUTION_STORAGE_KEY = 'ml_attribution';
const LEGACY_ATTRIBUTION_COOKIE = 'ml_attribution';

type DataLayerWindow = Window & { dataLayer?: Array<Record<string, unknown>> };
type StorageKind = 'localStorage' | 'sessionStorage';

export function pushDataLayer(payload: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  const w = window as DataLayerWindow;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push(payload);
}

function storageGet(kind: StorageKind, key: string): string | null {
  try {
    return window[kind].getItem(key);
  } catch {
    return null;
  }
}

function storageSet(kind: StorageKind, key: string, value: string): void {
  try {
    window[kind].setItem(key, value);
  } catch {
    // Storage blocked (private mode): attribution simply isn't persisted.
  }
}

function storageRemove(kind: StorageKind, key: string): void {
  try {
    window[kind].removeItem(key);
  } catch {
    // ignore
  }
}

export function hasAdsConsent(): boolean {
  return storageGet('localStorage', CONSENT_STORAGE_KEY) === 'granted';
}

function parseRecord(raw: string | null): AttributionRecord | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<AttributionRecord>;
    const ts = typeof value.ts === 'number' ? value.ts : 0;
    if (!ts || Date.now() - ts > ATTRIBUTION_TTL_MS) return null;
    return {
      ...sanitizeAttribution(value),
      referrer: typeof value.referrer === 'string' ? value.referrer : undefined,
      landing: typeof value.landing === 'string' ? value.landing : undefined,
      ts,
    };
  } catch {
    return null;
  }
}

/**
 * Stored record: 90 days in localStorage once the visitor accepted cookies (RGPD/ePrivacy),
 * otherwise only for the browser session.
 */
export function getAttributionRecord(): AttributionRecord | null {
  if (typeof window === 'undefined') return null;
  return (
    (hasAdsConsent() ? parseRecord(storageGet('localStorage', ATTRIBUTION_STORAGE_KEY)) : null) ??
    parseRecord(storageGet('sessionStorage', ATTRIBUTION_STORAGE_KEY))
  );
}

export function getStoredAttribution(): Attribution {
  return sanitizeAttribution(getAttributionRecord() ?? {});
}

function writeRecord(record: AttributionRecord): void {
  const raw = JSON.stringify(record);
  storageSet('sessionStorage', ATTRIBUTION_STORAGE_KEY, raw);
  if (hasAdsConsent()) storageSet('localStorage', ATTRIBUTION_STORAGE_KEY, raw);
}

/** Called after the visitor accepts cookies: keeps the current attribution for 90 days. */
export function persistAttribution(): void {
  const record = getAttributionRecord();
  if (record && hasAdsConsent()) storageSet('localStorage', ATTRIBUTION_STORAGE_KEY, JSON.stringify(record));
}

/** Called after the visitor rejects cookies: drops any persistent copy. */
export function clearPersistentAttribution(): void {
  storageRemove('localStorage', ATTRIBUTION_STORAGE_KEY);
}

/** Call on every page view: a new ad click replaces the record, otherwise the first touch is kept. */
export function captureAttribution(): AttributionRecord {
  // Drop the cookie used by the previous version; attribution now lives in Web Storage.
  if (typeof document !== 'undefined' && document.cookie.includes(`${LEGACY_ATTRIBUTION_COOKIE}=`)) {
    document.cookie = `${LEGACY_ATTRIBUTION_COOKIE}=; Max-Age=0; Path=/`;
  }
  const record = resolveAttributionRecord(getAttributionRecord(), {
    search: window.location.search,
    referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    pathname: window.location.pathname,
    now: Date.now(),
  });
  writeRecord(record);
  return record;
}

export function getCurrentOrigen(): Origen {
  return getOrigen(getAttributionRecord());
}

/** Reference code shared by WhatsApp chats and leads, e.g. ADS-NOMADA-7K2F. */
export function createRefId(servicio: string): { refId: string; origen: Origen } {
  const origen = getCurrentOrigen();
  return { refId: buildRefId(origen, servicio), origen };
}

export function getLeadTrackingPayload(servicio: string): LeadTrackingPayload {
  if (typeof window === 'undefined') {
    return { servicio, attribution: {}, pagePath: '', landingPage: '', origen: 'DIRECTO', refId: '' };
  }
  const record = getAttributionRecord();
  const { refId, origen } = createRefId(servicio);
  return {
    servicio,
    attribution: sanitizeAttribution(record ?? {}),
    pagePath: window.location.pathname,
    landingPage: record?.landing || window.location.pathname,
    origen,
    refId,
  };
}

/** Success path shared by every lead form: GTM generate_lead, then the conversion thank-you page. */
export function completeLeadSubmission(
  navigate: (url: string) => void,
  params: { servicio: string; locale: LeadLocale },
): string {
  pushDataLayer(buildGenerateLeadEvent(params.servicio, params.locale));
  const url = buildThankYouUrl(params.locale, params.servicio);
  navigate(url);
  return url;
}
